import React from 'react';

function firstNonEmptyPhysicalField(...candidates: unknown[]) {
  const present = candidates.find(
    (value) => value != null && `${value}`.trim() !== ''
  );
  return present ?? '';
}

export const disabledMailingAddressFields = [
  'mailingAddress.address1',
  'mailingAddress.address2',
  'mailingAddress.address3',
  'mailingAddress.city',
  'mailingAddress.state',
  'mailingAddress.zipCode',
];

export type DefaultMailingAddressShape = {
  address1?: string;
  address2?: string;
  address3?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
};

export default function useMailingAddressSync({
  formMethods,
  country,
  disabledFieldsProp = [],
  fieldPrefix = '',
  defaultMailingAddress,
}: {
  formMethods: any;
  country: string;
  disabledFieldsProp?: string[];
  fieldPrefix?: string;
  defaultMailingAddress?: DefaultMailingAddressShape;
}) {
  const prefix = fieldPrefix ? `${fieldPrefix}.` : '';

  const [isSameAddress, setIsSameAddress] = React.useState(false);
  const [disabledFields, setDisabledFields] =
    React.useState<string[]>(disabledFieldsProp);

  const address1 = formMethods.watch(`${prefix}address1`);
  const address2 = formMethods.watch(`${prefix}address2`);
  const address3 = formMethods.watch(`${prefix}address3`);
  const city = formMethods.watch(`${prefix}city`);
  const state = formMethods.watch(`${prefix}state`);
  const zipCode = formMethods.watch(`${prefix}zipCode`);

  const requiredFields = getRequiredFieldsByCountry(
    country,
    address1,
    city,
    state,
    zipCode
  );

  const areAnyPhysicalAddressFieldsEmpty = requiredFields.some(
    (val) => typeof val !== 'string' || !val || val.trim() === ''
  );

  const getPhysicalField = React.useCallback(
    (fieldName: string, watched: unknown) =>
      firstNonEmptyPhysicalField(
        formMethods.getValues(`${prefix}${fieldName}`),
        watched
      ),
    [formMethods, prefix]
  );

  const applyMailingDefaultsOrEmpty = React.useCallback(
    (markDirty: boolean) => {
      const base = defaultMailingAddress ?? {};
      formMethods.setValue(
        `${prefix}mailingAddress.address1`,
        base.address1 ?? '',
        { shouldDirty: markDirty }
      );
      formMethods.setValue(
        `${prefix}mailingAddress.address2`,
        base.address2 ?? ''
      );
      formMethods.setValue(
        `${prefix}mailingAddress.address3`,
        base.address3 ?? ''
      );
      formMethods.setValue(`${prefix}mailingAddress.city`, base.city ?? '');
      formMethods.setValue(`${prefix}mailingAddress.state`, base.state ?? '');
      formMethods.setValue(
        `${prefix}mailingAddress.country`,
        base.country ?? ''
      );
      formMethods.setValue(
        `${prefix}mailingAddress.zipCode`,
        base.zipCode ?? ''
      );
    },
    [defaultMailingAddress, formMethods, prefix]
  );

  const handleSameAddress = (markDirty = true) => {
    const mailingErrorPaths = [
      `${prefix}mailingAddress.address1`,
      `${prefix}mailingAddress.address2`,
      `${prefix}mailingAddress.address3`,
      `${prefix}mailingAddress.city`,
      `${prefix}mailingAddress.state`,
      `${prefix}mailingAddress.zipCode`,
    ];

    if (isSameAddress) {
      setDisabledFields(disabledFieldsProp || []);
      setIsSameAddress(false);
      formMethods.clearErrors(mailingErrorPaths);
      if (defaultMailingAddress !== undefined) {
        applyMailingDefaultsOrEmpty(markDirty);
      }
      return;
    }

    setDisabledFields([
      ...(disabledFieldsProp ?? []),
      ...disabledMailingAddressFields,
    ]);

    const primaryAddress1 = getPhysicalField('address1', address1);
    const primaryAddress2 = getPhysicalField('address2', address2);
    const primaryAddress3 = getPhysicalField('address3', address3);
    const primaryCity = getPhysicalField('city', city);
    const primaryState = getPhysicalField('state', state);
    const primaryZip = getPhysicalField('zipCode', zipCode);

    const hasCompletePhysical =
      country === 'US'
        ? Boolean(
            primaryAddress1 &&
              primaryCity &&
              primaryState &&
              `${primaryZip}`.trim()
          )
        : Boolean(primaryAddress1 && primaryCity && `${primaryZip}`.trim());

    if (!hasCompletePhysical) {
      setDisabledFields(disabledFieldsProp || []);
      return;
    }

    setIsSameAddress(true);
    formMethods.clearErrors(mailingErrorPaths);

    formMethods.setValue(`${prefix}mailingAddress.address1`, primaryAddress1, {
      shouldDirty: markDirty,
    });
    formMethods.setValue(`${prefix}mailingAddress.address2`, primaryAddress2);
    formMethods.setValue(`${prefix}mailingAddress.address3`, primaryAddress3);
    formMethods.setValue(`${prefix}mailingAddress.city`, primaryCity);
    formMethods.setValue(`${prefix}mailingAddress.state`, primaryState);
    formMethods.setValue(`${prefix}mailingAddress.zipCode`, primaryZip);
    formMethods.setValue(`${prefix}mailingAddress.country`, country);
  };

  const sawIncompletePhysicalRef = React.useRef(false);
  const previousPhysicalKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const complete =
      country === 'US'
        ? Boolean(
            address1 &&
              city &&
              state &&
              zipCode &&
              `${address1}`.trim() &&
              `${city}`.trim() &&
              `${state}`.trim() &&
              `${zipCode}`.trim()
          )
        : Boolean(
            address1 &&
              city &&
              zipCode &&
              `${address1}`.trim() &&
              `${city}`.trim() &&
              `${zipCode}`.trim()
          );

    if (!complete) {
      sawIncompletePhysicalRef.current = true;
      previousPhysicalKeyRef.current = null;
      return;
    }

    const physicalKey = [
      address1,
      address2,
      address3,
      city,
      state,
      zipCode,
    ].join('\u0001');

    const prevKey = previousPhysicalKeyRef.current;

    if (prevKey === null) {
      previousPhysicalKeyRef.current = physicalKey;
      if (sawIncompletePhysicalRef.current) {
        setIsSameAddress(false);
        setDisabledFields([]);
      }
      return;
    }

    previousPhysicalKeyRef.current = physicalKey;

    if (prevKey !== physicalKey) {
      setIsSameAddress(false);
      setDisabledFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exclude country so country-only changes do not reset same-as after hydration
  }, [address1, address2, address3, city, state, zipCode]);

  return {
    isSameAddress,
    disabledFields,
    areAnyPhysicalAddressFieldsEmpty,
    handleSameAddress,
  };
}

function getRequiredFieldsByCountry(
  country: string,
  address1: string,
  city: string,
  state: string,
  zipCode: string
) {
  switch (country) {
    case 'UK':
      return [address1, city, zipCode];
    case 'AU':
      return [address1, city, state, zipCode];
    case 'US':
    default:
      return [address1, city, state, zipCode];
  }
}
